import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Button } from "@baluu/ui/components/button";
import { Spinner } from "@baluu/ui/components/spinner";
import type { ViewContext } from "../types";
import { BookingApiError, bookingJson, messageOf, sendJson } from "../api";
import { BookingLoading, BookingProblem, Notice } from "../parts";
import { isOtherSchool } from "../school";
import { UNSUBSCRIBE_FAILED, listingTitleOf, unsubscribeFailure, type WaitingListEntry } from "./waiting-list/unsubscribe";

// /waiting-list/unsubscribe/<entry-id>: leave a waiting list from its
// emails. Port of v1's page: "Are you sure?", then "You've been
// unsubscribed". The entry's id is what lets the customer in; the API
// answers only for the site's own school (its slug in the address).

export function WaitingListUnsubscribeView({ ctx, entryId }: { ctx: ViewContext; entryId: string }) {
  const [done, setDone] = useState(false);
  const slug = ctx.businessSlug.trim();
  const url = `${ctx.api}/widget/${encodeURIComponent(slug)}/waiting-list/${encodeURIComponent(entryId)}`;
  const entry = useQuery({
    queryKey: ["booking", "waiting-list-entry", ctx.api, slug, entryId],
    queryFn: () => bookingJson<WaitingListEntry>(url),
    // Once left, the entry is gone: don't re-read it (a 404 would replace
    // the confirmation).
    enabled: !!slug && !done,
    retry: (count, error) => !(error instanceof BookingApiError && error.status < 500) && count < 2,
  });
  const leave = useMutation({
    mutationFn: () => sendJson(url, "DELETE"),
    onSuccess: () => setDone(true),
    onError: (error) => {
      if (unsubscribeFailure(error) === "done") setDone(true);
    },
  });

  const gone = (
    <BookingProblem title="We couldn’t find this waiting list entry" message="You may already have left the waiting list." />
  );
  if (!slug) return gone;
  if (entry.isPending) return <BookingLoading rows={1} />;
  if (entry.isError) {
    return entry.error instanceof BookingApiError && entry.error.status === 404 ? (
      gone
    ) : (
      <BookingProblem
        title="We couldn’t load this page"
        message={messageOf(entry.error, "Please try again in a moment.")}
        onRetry={() => void entry.refetch()}
      />
    );
  }
  if (isOtherSchool(ctx.businessId, entry.data?.courseId?.schoolId)) return gone;

  const title = listingTitleOf(entry.data);
  return (
    <div className="mx-auto max-w-lg space-y-4 rounded-lg border bg-card p-8 text-center">
      {done ? (
        <>
          <h1 className="text-2xl font-semibold">You’ve been unsubscribed</h1>
          <p className="text-muted-foreground">You’ll no longer receive notifications about {title}.</p>
          <Button asChild>
            <a href={ctx.links.home()}>Home</a>
          </Button>
        </>
      ) : (
        <>
          <h1 className="text-2xl font-semibold">Are you sure you want to unsubscribe?</h1>
          <p className="text-muted-foreground">If you unsubscribe you’ll stop receiving notifications about {title}.</p>
          <Button onClick={() => leave.mutate()} disabled={leave.isPending}>
            {leave.isPending && <Spinner className="mr-2 size-4" />}
            Unsubscribe
          </Button>
          {leave.isError && unsubscribeFailure(leave.error) === "retry" && <Notice tone="error">{UNSUBSCRIBE_FAILED}</Notice>}
        </>
      )}
    </div>
  );
}
